import { ModelDto } from '@vatos-pas/common';

import { TRIM_OPTION_RESET } from './constants';
import { JobCreateFormFields } from './types';

// The DTO declares the default costs/footage as required numbers, but the API
// serves null when a model has no default, so the source widens them to null.
export type ModelDefaultsSource = Pick<
  ModelDto,
  | 'highCeiling'
  | 'garageFirst'
  | 'screwInspection'
  | 'hasTrim'
  | 'hasExteriorPaint'
  | 'hasInteriorOptions'
  | 'hasExteriorOptions'
  | 'hasTrimOptions'
> & {
  defaultTotalSquareFootage: number | null;
  defaultInteriorOptionCost: number | null;
  defaultExteriorOptionCost: number | null;
  defaultTrimOptionCost: number | null;
  defaultExteriorSidingPaintAmount: number | string | null;
};

export type ModelSeededJobFields = Pick<
  JobCreateFormFields,
  | 'highCeiling'
  | 'garageFirst'
  | 'screwInspection'
  | 'hasTrim'
  | 'hasExteriorPaint'
  | 'hasInteriorOptions'
  | 'hasExteriorOptions'
  | 'totalSquareFootage'
  | 'interiorOptionCost'
  | 'exteriorOptionCost'
  | 'hasTrimOptions'
  | 'trimOptionCost'
  | 'exteriorSidingPaintAmount'
>;

/**
 * Shared by every model-selection path (project-details section and SP-order
 * import) so a model seeds the same fields everywhere.
 */
export const modelDefaultsToJobFields = (
  model: ModelDefaultsSource,
): ModelSeededJobFields => {
  return {
    highCeiling: model.highCeiling,
    garageFirst: model.garageFirst,
    screwInspection: model.screwInspection,
    hasTrim: model.hasTrim,
    hasExteriorPaint: model.hasExteriorPaint,
    hasInteriorOptions: model.hasInteriorOptions,
    hasExteriorOptions: model.hasExteriorOptions,
    totalSquareFootage: model.defaultTotalSquareFootage ?? undefined,
    interiorOptionCost: model.defaultInteriorOptionCost || 0,
    exteriorOptionCost: model.defaultExteriorOptionCost || 0,
    exteriorSidingPaintAmount:
      model.defaultExteriorSidingPaintAmount === null
        ? undefined
        : Number(model.defaultExteriorSidingPaintAmount),
    // The model subscriber already forces Trim Option off (and its cost null)
    // whenever Trim is off; this guard only mirrors that rule for stale data.
    ...(model.hasTrim && model.hasTrimOptions
      ? {
          hasTrimOptions: true,
          trimOptionCost: model.defaultTrimOptionCost ?? undefined,
        }
      : TRIM_OPTION_RESET),
  };
};
